/**
 * <kik-related-categories>
 * A dependency-free scroll-snap carousel for the KIK Related Categories section.
 * Desktop: items grow to fill the row when they fit; when there are more than the
 * row can hold they become fixed-width and scroll horizontally (matching the
 * Figma). Mobile: fixed-width items always scroll horizontally.
 *
 * Progressive enhancement — the track scrolls natively (touch / trackpad) even
 * without JS; this wires the prev/next arrows (advance one item), the progress
 * bar (fills from the left as you scroll), and hides the whole control row when
 * the track doesn't overflow (the desktop "few items" state has no controls).
 */
if (!customElements.get('kik-related-categories')) {
  customElements.define(
    'kik-related-categories',
    class KikRelatedCategories extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.section-kik-rc__track');
        this.controls = this.querySelector('.section-kik-rc__controls');
        this.prevBtn = this.querySelector('[data-dir="prev"]');
        this.nextBtn = this.querySelector('[data-dir="next"]');
        this.fill = this.querySelector('.section-kik-rc__progress-fill');
        if (!this.track) return;

        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByItem(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByItem(1));

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.update);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.update);
        }
        // Recompute once everything (fonts) has settled so the fill length
        // reflects the true scroll width.
        window.addEventListener('load', this.update);
        this.update();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.update);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.update);
        window.removeEventListener('load', this.update);
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      // Advance by exactly one item (item width + column gap).
      scrollByItem(direction) {
        const slide = this.track.querySelector('.section-kik-rc__slide');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = slide ? slide.offsetWidth + gap : this.track.clientWidth;
        this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      update() {
        const max = this.maxScroll;
        const hasOverflow = max > 1;

        // No overflow → no controls at all (desktop "few items" state).
        if (this.controls) this.controls.hidden = !hasOverflow;

        // Progress bar FILLS from the left: shows a small segment by default
        // (START_FILL) and grows to 100% as the track scrolls to the last item.
        if (this.fill) {
          const START_FILL = 0.2; // ~20% shown at rest
          const progress = hasOverflow ? this.track.scrollLeft / max : 0;
          const width = (START_FILL + (1 - START_FILL) * progress) * 100;
          this.fill.style.width = Math.min(100, width) + '%';
        }

        if (this.prevBtn) this.prevBtn.disabled = !hasOverflow || this.track.scrollLeft <= 1;
        if (this.nextBtn) this.nextBtn.disabled = !hasOverflow || this.track.scrollLeft >= max - 1;
      }
    }
  );
}
